import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Shield, 
  Lock, 
  Eye, 
  FileCheck, 
  Server, 
  Key,
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Database,
  Fingerprint,
  Globe2
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { useTranslation } from "react-i18next";
import { Seo } from "@/components/Seo";
import { Badge } from "@/components/ui/badge";

export default function Security() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const securityFeatures = [
    {
      icon: Lock,
      title: t("security.encryptionTitle"),
      description: t("security.encryptionDesc")
    },
    {
      icon: Key,
      title: t("security.features.twoFactorTitle"),
      description: t("security.features.twoFactorDesc")
    },
    {
      icon: Shield,
      title: t("security.features.passwordProtectedTitle"),
      description: t("security.features.passwordProtectedDesc")
    },
    {
      icon: Server,
      title: t("security.features.infrastructureTitle"),
      description: t("security.features.infrastructureDesc")
    },
    {
      icon: Eye,
      title: t("security.features.privacyByDesignTitle"),
      description: t("security.features.privacyByDesignDesc")
    },
    {
      icon: FileCheck,
      title: t("security.features.auditsTitle"),
      description: t("security.features.auditsDesc")
    }
  ];

  const certifications = [
    t("security.certificationTitle"),
    t("security.complianceTitle"),
    t("security.certifications.ccpa"),
    t("security.certifications.iso"),
    t("security.certifications.hipaa"),
    t("security.certifications.pci")
  ];

  const dataProtection = [
    {
      title: t("security.dataProtection.restTitle"),
      description: t("security.dataProtection.restDesc")
    },
    {
      title: t("security.dataProtection.transitTitle"),
      description: t("security.dataProtection.transitDesc")
    },
    {
      title: t("security.dataProtection.residencyTitle"),
      description: t("security.dataProtection.residencyDesc")
    },
    {
      title: t("security.dataProtection.deletionTitle"),
      description: t("security.dataProtection.deletionDesc")
    }
  ];

  const trustStats = [
    { label: "Meeting access", value: "Controlled" },
    { label: "Media transport", value: "Encrypted" },
    { label: "Admin actions", value: "Audited" },
  ];

  const assuranceStats = [
    { label: "Encryption", value: "At rest + transit" },
    { label: "Access", value: "Role governed" },
    { label: "Compliance", value: "Reviewed" },
  ];

  const securityFeatureMeta = [
    {
      label: "Cryptography",
      image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=900&q=80"
    },
    {
      label: "Identity",
      image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=900&q=80"
    },
    {
      label: "Meeting access",
      image: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=900&q=80"
    },
    {
      label: "Infrastructure",
      image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=900&q=80"
    },
    {
      label: "Privacy",
      image: "https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?auto=format&fit=crop&w=900&q=80"
    },
    {
      label: "Assurance",
      image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=900&q=80"
    },
  ];

  const privacyCommitments = [
    {
      title: t("security.privacyCommitment.sellDataTitle"),
      description: t("security.privacyCommitment.sellDataDesc")
    },
    {
      title: t("security.privacyCommitment.ownDataTitle"),
      description: t("security.privacyCommitment.ownDataDesc")
    },
    {
      title: t("security.privacyCommitment.transparentTitle"),
      description: t("security.privacyCommitment.transparentDesc")
    },
    {
      title: t("security.privacyCommitment.minimalTitle"),
      description: t("security.privacyCommitment.minimalDesc")
    }
  ];

  return (
    <div className="marketing-page min-h-screen bg-background">
      <Seo
        title="Security - MeetHub trust and protection"
        description="Explore MeetHub security architecture, privacy commitments, certifications, and data protection practices."
        canonicalPath="/security"
      />
      <Navigation />
      <main>
          {/* Hero Section */}
          <section className="relative overflow-hidden bg-gradient-soft pt-24 pb-10 sm:pt-24 sm:pb-12 border-b">
            <div className="absolute inset-0 bg-gradient-hero pointer-events-none" aria-hidden />
            <div className="container relative max-w-6xl">
              <div className="grid lg:grid-cols-[1fr_0.9fr] gap-10 items-center">
                <div>
                  <Badge variant="outline" className="mb-4">Security</Badge>
                  <div className="h-14 w-14 rounded-2xl bg-primary text-primary-foreground inline-flex items-center justify-center shadow-sm mb-5">
                    <Shield className="h-7 w-7" />
                  </div>
                  <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">{t("security.title")}</h1>
                  <p className="mt-5 text-lg text-muted-foreground max-w-xl leading-relaxed">
                    {t("security.subtitle")}
                  </p>
                  <div className="mt-7 grid grid-cols-3 gap-3 max-w-xl">
                    {trustStats.map((stat) => (
                      <div key={stat.label} className="rounded-xl border bg-card/90 p-4 text-center shadow-xs">
                        <p className="text-sm font-semibold">{stat.value}</p>
                        <p className="mt-1 text-xs text-muted-foreground">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="relative overflow-hidden rounded-2xl border bg-card shadow-sm min-h-[430px]">
                  <img
                    src="https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=1100&q=80"
                    alt="Security operations dashboard"
                    className="absolute inset-0 h-full w-full object-cover"
                    loading="eager"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/45 to-foreground/10" />
                  <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8 text-primary-foreground">
                    <Badge className="mb-4 bg-background/90 text-foreground hover:bg-background/90">
                      Protected by default
                    </Badge>
                    <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
                      Product security that stays visible to hosts and admins.
                    </h2>
                    <p className="mt-3 text-sm sm:text-base text-primary-foreground/85 max-w-md leading-relaxed">
                      Access rules, media safeguards, role controls, and audit practices work together across the meeting lifecycle.
                    </p>
                    <div className="mt-6 grid grid-cols-3 gap-3">
                      {assuranceStats.map((stat) => (
                        <div key={stat.label} className="rounded-xl border border-white/20 bg-white/10 p-3 backdrop-blur">
                          <p className="text-sm font-semibold">{stat.value}</p>
                          <p className="mt-1 text-xs text-primary-foreground/75">{stat.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Security Features */}
          <section className="py-12 sm:py-14">
            <div className="container max-w-6xl">
              <div className="max-w-2xl mb-10">
                <p className="text-xs font-semibold uppercase tracking-widest text-primary">Protection layers</p>
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">{t("security.sections.featuresTitle")}</h2>
                <p className="mt-3 text-muted-foreground">Core safeguards are built into the meeting experience, not bolted on after setup.</p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {securityFeatures.map((feature, index) => (
                  <Card key={index} className="group overflow-hidden border-border/70 hover:border-primary/30 hover:shadow-lg transition-all">
                    <div className="relative h-40 overflow-hidden bg-secondary">
                      <img
                        src={securityFeatureMeta[index].image}
                        alt={`${feature.title} security preview`}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/35 to-transparent" />
                      <div className="absolute left-4 bottom-4 h-11 w-11 rounded-lg bg-background/90 text-primary shadow-sm inline-flex items-center justify-center backdrop-blur">
                        <feature.icon className="h-5 w-5" />
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <p className="text-xs font-semibold uppercase tracking-widest text-primary">
                        {securityFeatureMeta[index].label}
                      </p>
                      <h3 className="font-semibold text-lg">{feature.title}</h3>
                      <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* Certifications */}
          <section className="py-12 sm:py-14 bg-secondary/30 border-y">
            <div className="container max-w-6xl">
              <div className="rounded-2xl border bg-foreground text-background shadow-lg overflow-hidden">
                <div className="grid lg:grid-cols-[0.85fr_1.15fr]">
                  <div className="p-6 sm:p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-background/15">
                    <Badge className="mb-5 bg-background/10 text-background hover:bg-background/10 border border-background/20">
                      Compliance ready
                    </Badge>
                    <h2 className="text-3xl font-bold tracking-tight">{t("security.sections.certificationsTitle")}</h2>
                    <p className="mt-4 text-background/70 leading-relaxed">{t("security.sections.certificationsDesc")}</p>
                    <div className="mt-8 space-y-3">
                      {["Policy alignment", "Evidence reviews", "Vendor diligence"].map((item) => (
                        <div key={item} className="flex items-center justify-between rounded-xl border border-background/15 bg-background/5 px-4 py-3">
                          <span className="text-sm font-medium">{item}</span>
                          <CheckCircle2 className="h-4 w-4 text-primary-glow" />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 bg-background p-5 sm:p-6 lg:p-8 text-foreground">
                    {certifications.map((cert, index) => (
                      <div key={index} className="rounded-xl border bg-card p-4 hover:border-primary/30 hover:shadow-sm transition-all">
                        <FileCheck className="h-5 w-5 text-primary mb-3" />
                        <span className="text-sm font-medium text-muted-foreground">{cert}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Data Protection */}
          <section className="py-12 sm:py-14">
            <div className="container max-w-6xl">
              <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-8 items-start">
                <div className="sticky top-28 rounded-2xl border bg-gradient-soft p-6 sm:p-8 shadow-sm">
                  <Badge variant="outline" className="mb-4">Data lifecycle</Badge>
                  <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">{t("security.sections.dataProtectionTitle")}</h2>
                  <p className="mt-3 text-muted-foreground leading-relaxed">
                    Meeting data needs protection across storage, transit, residency, and deletion. MeetHub keeps those controls visible and practical.
                  </p>
                  <div className="mt-6 overflow-hidden rounded-xl border bg-card">
                    <img
                      src="https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=1000&q=80"
                      alt="Secure data operations dashboard"
                      className="h-56 w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
                <div className="grid gap-4">
                  {dataProtection.map((item, index) => (
                    <Card key={index} className="border-border/70 hover:border-primary/30 hover:shadow-md transition-all">
                      <CardContent className="p-5 sm:p-6">
                        <div className="relative flex gap-4">
                          {index < dataProtection.length - 1 && (
                            <span className="absolute left-[22px] top-14 h-8 w-px bg-border" aria-hidden />
                          )}
                          <div className="h-11 w-11 rounded-full bg-primary text-primary-foreground inline-flex items-center justify-center flex-shrink-0 shadow-sm">
                            {index === 0 ? (
                              <Database className="h-5 w-5" />
                            ) : index === 1 ? (
                              <Lock className="h-5 w-5" />
                            ) : index === 2 ? (
                              <Globe2 className="h-5 w-5" />
                            ) : (
                              <FileCheck className="h-5 w-5" />
                            )}
                          </div>
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-widest text-primary">Layer {index + 1}</p>
                            <h3 className="mt-1 font-semibold text-lg">{item.title}</h3>
                            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{item.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Privacy Commitment */}
          <section className="py-12 sm:py-14 bg-secondary/30 border-y">
            <div className="container max-w-6xl">
              <div className="max-w-2xl mb-10">
                <p className="text-xs font-semibold uppercase tracking-widest text-primary">Privacy promise</p>
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">{t("security.sections.privacyCommitmentTitle")}</h2>
              </div>
              <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-8 items-stretch">
                <div className="grid md:grid-cols-2 gap-5">
                  {privacyCommitments.map((item, index) => (
                    <Card key={item.title} className={`border-border/70 hover:border-primary/30 hover:shadow-md transition-all ${index === 0 ? "bg-gradient-soft" : "bg-card"}`}>
                      <CardContent className="p-6">
                        <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary inline-flex items-center justify-center mb-4">
                          {index % 2 === 0 ? <Eye className="h-5 w-5" /> : <Fingerprint className="h-5 w-5" />}
                        </div>
                        <h3 className="font-semibold text-lg">{item.title}</h3>
                        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{item.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <div className="relative overflow-hidden rounded-2xl border bg-card shadow-sm min-h-[420px]">
                  <img
                    src="https://images.unsplash.com/photo-1551836022-8b2858c9c69b?auto=format&fit=crop&w=1000&q=80"
                    alt="Private business meeting"
                    className="absolute inset-0 h-full w-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/85 via-foreground/30 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8 text-primary-foreground">
                    <Badge className="mb-4 bg-background/90 text-foreground hover:bg-background/90">
                      User control
                    </Badge>
                    <h3 className="text-2xl sm:text-3xl font-bold tracking-tight">
                      Clear boundaries for meeting data.
                    </h3>
                    <p className="mt-3 text-sm text-primary-foreground/85 leading-relaxed">
                      Privacy choices should be easy to understand before teams invite guests, record sessions, or share follow-ups.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Responsible Disclosure */}
          <section className="py-12 sm:py-14">
            <div className="container max-w-6xl">
              <div className="relative overflow-hidden rounded-2xl border bg-gradient-soft shadow-sm">
                <div className="absolute inset-0 bg-gradient-hero pointer-events-none" aria-hidden />
                <div className="relative p-6 sm:p-8 lg:p-10">
                <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-8 items-center">
                  <div>
                    <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary inline-flex items-center justify-center mb-5">
                      <AlertTriangle className="h-6 w-6" />
                    </div>
                    <Badge variant="outline" className="mb-4">Responsible disclosure</Badge>
                    <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("security.sections.disclosureTitle")}</h2>
                    <p className="mt-3 text-muted-foreground leading-relaxed">{t("security.sections.disclosureDesc")}</p>
                  </div>
                  <div className="rounded-2xl border bg-gradient-soft p-5 sm:p-6">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {t("security.sections.disclosureBody")}
                    </p>
                    <div className="mt-5 rounded-xl border bg-background p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <code className="text-sm font-semibold">security@meethub.com</code>
                        <Badge variant="secondary" className="w-fit">Reviewed intake</Badge>
                      </div>
                    </div>
                    <p className="mt-4 text-sm text-muted-foreground">
                      {t("security.sections.disclosureInstructions")}
                    </p>
                  </div>
                </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-14 sm:py-16">
            <div className="container max-w-6xl">
              <div className="relative overflow-hidden rounded-2xl border bg-foreground p-6 sm:p-10 lg:p-12 text-background shadow-lg">
                <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-primary/30 to-transparent pointer-events-none" aria-hidden />
                <div className="relative grid lg:grid-cols-[1fr_auto] gap-8 items-center">
                  <div>
                    <Badge className="mb-4 bg-background/10 text-background hover:bg-background/10 border border-background/20">
                      Talk security
                    </Badge>
                    <h2 className="text-3xl sm:text-5xl font-bold tracking-tight">{t("security.sections.ctaTitle")}</h2>
                    <p className="mt-4 max-w-2xl text-background/75 leading-relaxed">
                      {t("security.sections.ctaSubtitle")}
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row lg:flex-col gap-3 min-w-[220px]">
                    <Button size="lg" variant="secondary" className="h-12 px-7" onClick={() => navigate("/contact")}>
                      {t("security.sections.ctaContact")} <ArrowRight className="h-4 w-4" />
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      className="h-12 px-7 bg-transparent border-background/30 text-background hover:bg-background/10 hover:text-background"
                      onClick={() => navigate("/privacy")}
                    >
                      {t("security.sections.ctaPrivacy")}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>
      </main>
      <Footer />
    </div>
  );
}


